/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */
/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes an order for a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',
  BUY_NOW: 'transition/buy-now',
  MAKE_AN_OFFER_BY_CUSTOMER: 'transition/customer-make-a-offer',
  OFFER_COUNTERED_BY_PROVIDER: 'transition/provider-counter-offer',
  OFFER_ACCEPTED_BY_PROVIDER: 'transition/provider-accept-offer',
  OFFER_REJECTED_BY_PROVIDER: 'transition/provider-reject-offer',
  COUNTER_OFFER_REJECTED_BY_CUSTOMER: 'transition/customer-reject-counter-offer',
  COUNTER_OFFER_ACCEPTED_BY_CUSTOMER: 'transition/customer-accept-counter-offer',
  OFFER_REJECT_BY_OPERATOR: 'transition/offer-recject-by-operator',
  EXPIRE_OFFER_REJECTED_BY_OPERATOR: 'transition/expire-offer-reject-by-operator',
  COUNTER_BY_CUSTOMER_2: 'transition/customer-counter-offer-2',
  CONFIRM_PAYMENT_AFTER_OFFER_ACCEPT: 'transition/confirm-payment-after-offer-accepted',
  OFFER_EXPIRE: 'transition/expire-offer',
  COUNTER_OFFER_EXPIRE: 'transition/expire-counter-offer',
  ORDER_CANCEL_BY_PROVIDER: 'transition/cancel-by-provider',
  ORDER_CANCEL_BY_CUSTOMER: 'transition/cancel-by-customer',
  SHIPPING_LABEL_CREATE: 'transition/create-shipping-label',
  OPERATOR_MARK_SHIPPED: "transition/operator-mark-shipped",

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  INQUIRE_MESSAGE: 'transition/inquire-message',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  ORDER_PURCHASED: 'transition/order-purchased',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider or opeartor can mark the product shipped/delivered
  MARK_DELIVERED: 'transition/mark-delivered',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // Customer can mark the product received (e.g. picked up from provider)
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Automatic cancellation happens if none marks the delivery happened
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel the purchase before product has been marked as delivered / received
  CANCEL: 'transition/cancel',
  CANCEL_BY_OPERATOR: 'transition/cancel-by-operator',

  // If provider has marked the product delivered (e.g. shipped),
  // customer can then mark the product received
  MARK_RECEIVED: 'transition/mark-received',

  // If customer doesn't mark the product received manually, it can happen automatically
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // When provider has marked the product delivered, customer or operator can dispute the transaction
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // If nothing is done to disputed transaction it ends up to Canceled state
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  // System moves transaction automatically from received state to complete state
  // This makes it possible to to add notifications to that single transition.
  AUTO_COMPLETE: 'transition/auto-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',

  OPERATOR_CANCEL_WITH_REFUND_AFTER_OFFER: 'transition/operator-cancel-with-refund-after-offer',
  OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_OFFER: 'transition/operator-cancel-without-refund-after-offer',

  OPERATOR_CANCEL_WITH_REFUND_AFTER_COUNTER_OFFER: 'transition/operator-cancel-with-refund-after-counter-offer',
  OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_COUNTER_OFFER: 'transition/operator-cancel-without-refund-after-counter-offer',

  OPERATOR_CANCEL_WITH_REFUND: 'transition/operator-cancel-with-refund',
  OPERATOR_CANCEL_WITHOUT_REFUND: 'transition/operator-cancel-without-refund',

  OPERATOR_CANCEL_WITH_REFUND_AFTER_PURCHASED: 'transition/operator-cancel-with-refund-after-purchased',
  OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_PURCHASED: 'transition/operator-cancel-without-refund-after-purchased',

  OPERATOR_CANCEL_WITH_REFUND_AFTER_SHIPPING_LABEL_CREATED: 'transition/operator-cancel-with-refund-after-shipping-label-created',
  OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_SHIPPING_LABEL_CREATED: 'transition/operator-cancel-without-refund-after-shipping-label-created',

  OPERATOR_CANCEL_WITH_REFUND_AFTER_ORDER_SHIPPED: 'transition/operator-cancel-with-refund-after-order-shipped',
  OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_ORDER_SHIPPED: 'transition/operator-cancel-without-refund-after-order-shipped',

  OPERATOR_CANCEL_WITH_REFUND_AFTER_DELIVERED: 'transition/operator-cancel-with-refund-after-delivered',
  OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_DELIVERED: 'transition/operator-cancel-without-refund-after-delivered',

  OPERATOR_CANCEL_WITH_REFUND_AFTER_RECEIVED: 'transition/operator-cancel-with-refund-after-received',
  OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_RECEIVED: 'transition/operator-cancel-without-refund-after-received',

};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  INQUIRY_MESSAGES: 'inquiry-message',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  PENDING_OFFER: 'pending-offer',
  OFFER_REJECTED: 'offer-rejected',
  OFFER_EXPIRED: 'offer-expired',
  PENDING_COUNTER_OFFER: 'pending-counter-offer',
  COUNTER_OFFER_REJECTED: 'counter-offer-rejected',
  SHIPPING_LABEL_CREATED: 'shipping-label-created',
  REQUEST_TO_CANCEL: 'request-to-cancel',
  REQUEST_TO_REJECT_OFFER: 'request-to-reject-offer',
  OFFER_CANCELED: 'offer-canceled',
  ORDER_SHIPPED: "order-shipped",
  OFFER_ACCEPTED: "offer-accepted"
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'dots-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.INQUIRE_MESSAGE]: states.INQUIRY_MESSAGES,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.MAKE_AN_OFFER_BY_CUSTOMER]: states.PENDING_OFFER,
        [transitions.BUY_NOW]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_OFFER]: {
      on: {
        [transitions.OFFER_ACCEPTED_BY_PROVIDER]: states.OFFER_ACCEPTED,
        [transitions.OFFER_REJECTED_BY_PROVIDER]: states.OFFER_REJECTED,
        [transitions.OFFER_COUNTERED_BY_PROVIDER]: states.PENDING_COUNTER_OFFER,
        [transitions.OFFER_EXPIRE]: states.OFFER_EXPIRED,
        [transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_OFFER]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_OFFER]: states.CANCELED
      },
    },
    [states.PENDING_COUNTER_OFFER]: {
      on: {
        [transitions.COUNTER_OFFER_ACCEPTED_BY_CUSTOMER]: states.OFFER_ACCEPTED,
        [transitions.COUNTER_OFFER_REJECTED_BY_CUSTOMER]: states.COUNTER_OFFER_REJECTED,
        [transitions.COUNTER_BY_CUSTOMER_2]: states.PENDING_OFFER,
        [transitions.COUNTER_OFFER_EXPIRE]: states.OFFER_EXPIRED,
        [transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_COUNTER_OFFER]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_COUNTER_OFFER]: states.CANCELED
      },
    },

    [states.OFFER_ACCEPTED]: {
      on: {
        [transitions.ORDER_PURCHASED]: states.PURCHASED
      }
    },
    // [states.INQUIRY]: {
    //   on: {
    //     // [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
    //     [transitions.BUY_NOW]: states.PENDING_PAYMENT,
    //   },
    // },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
        [transitions.OPERATOR_CANCEL_WITHOUT_REFUND]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_WITH_REFUND]: states.CANCELED
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.OFFER_REJECTED]: {},
    [states.OFFER_EXPIRED]: {},
    [states.PURCHASED]: {
      on: {
        [transitions.ORDER_CANCEL_BY_PROVIDER]: states.REQUEST_TO_CANCEL,
        [transitions.ORDER_CANCEL_BY_CUSTOMER]: states.REQUEST_TO_CANCEL,
        [transitions.AUTO_CANCEL]: states.REQUEST_TO_CANCEL,
        [transitions.CANCEL]: states.CANCELED,
        [transitions.SHIPPING_LABEL_CREATE]: states.SHIPPING_LABEL_CREATED,
        [transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_PURCHASED]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_PURCHASED]: states.CANCELED
      },
    },
    [states.REQUEST_TO_CANCEL]: {
      on: {
        [transitions.CANCEL_BY_OPERATOR]: states.CANCELED,
      },
    },

    [states.CANCELED]: {},

    [states.SHIPPING_LABEL_CREATED]: {
      on: {
        [transitions.OPERATOR_MARK_SHIPPED]: states.ORDER_SHIPPED,
        [transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_SHIPPING_LABEL_CREATED]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_SHIPPING_LABEL_CREATED]: states.CANCELED
      },
    },

    [states.ORDER_SHIPPED]: {
      on: {
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_ORDER_SHIPPED]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_ORDER_SHIPPED]: states.CANCELED
      },
    },


    [states.DELIVERED]: {
      on: {
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_DELIVERED]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_DELIVERED]: states.CANCELED
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
        [transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_RECEIVED]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_RECEIVED]: states.CANCELED
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED,
        // [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    // [states.REVIEWED_BY_CUSTOMER]: {
    //   on: {
    //     [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
    //     [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
    //   },
    // },
    // [states.REVIEWED_BY_PROVIDER]: {
    //   on: {
    //     [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
    //     [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
    //   },
    // },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.AUTO_CANCEL,
    transitions.CANCEL,
    transitions.CANCEL_BY_OPERATOR,
    transitions.MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED,
    transitions.OPERATOR_MARK_SHIPPED,
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_COMPLETE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.MAKE_AN_OFFER_BY_CUSTOMER,
    transitions.OFFER_COUNTERED_BY_PROVIDER,
    transitions.OFFER_EXPIRE,
    transitions.OFFER_ACCEPTED_BY_PROVIDER,
    transitions.OFFER_REJECTED_BY_PROVIDER,
    transitions.OFFER_EXPIRE,
    transitions.COUNTER_BY_CUSTOMER_2,
    transitions.COUNTER_OFFER_ACCEPTED_BY_CUSTOMER,
    transitions.COUNTER_OFFER_EXPIRE,
    transitions.COUNTER_OFFER_REJECTED_BY_CUSTOMER,
    transitions.ORDER_CANCEL_BY_PROVIDER,
    transitions.ORDER_CANCEL_BY_CUSTOMER,
    transitions.OFFER_REJECT_BY_OPERATOR,
    transitions.ORDER_PURCHASED,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_OFFER,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_OFFER,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_COUNTER_OFFER,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_COUNTER_OFFER,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_PURCHASED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_PURCHASED,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_SHIPPING_LABEL_CREATED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_SHIPPING_LABEL_CREATED,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_ORDER_SHIPPED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_ORDER_SHIPPED,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_DELIVERED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_DELIVERED,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_RECEIVED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_RECEIVED,
    transitions.SHIPPING_LABEL_CREATE
    // transitions.BUY_NOW
  ].includes(transition);
};
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.BUY_NOW,
    transitions.MAKE_AN_OFFER_BY_CUSTOMER,
  ].includes(transition);
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.CANCEL_BY_OPERATOR,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.OFFER_EXPIRE,
    transitions.COUNTER_OFFER_EXPIRE,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_OFFER,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_OFFER,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_COUNTER_OFFER,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_COUNTER_OFFER,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_PURCHASED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_PURCHASED,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_SHIPPING_LABEL_CREATED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_SHIPPING_LABEL_CREATED,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_ORDER_SHIPPED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_ORDER_SHIPPED,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_DELIVERED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_DELIVERED,
    transitions.OPERATOR_CANCEL_WITH_REFUND_AFTER_RECEIVED,
    transitions.OPERATOR_CANCEL_WITHOUT_REFUND_AFTER_RECEIVED
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PURCHASED];
